import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { Phone, Mic, MicOff } from 'lucide-react';
import { CallType, CallMode } from '../../routes/agent/RolePlay';
import { scriptRegistry } from '../../config/scripts';

interface ModernActiveCallHeaderProps {
  agentInfo: any;
  callType: CallType;
  callMode?: CallMode;
  scriptId?: string;
  callDuration: string;
  isSpeaking: boolean;
  isMuted?: boolean;
  onEndCall: () => void;
  onToggleMute?: () => void;
}

export const ModernActiveCallHeader: React.FC<ModernActiveCallHeaderProps> = ({
  agentInfo,
  callType,
  callMode,
  scriptId,
  callDuration,
  isSpeaking,
  isMuted = false,
  onEndCall,
  onToggleMute,
}) => {
  const isScriptedMode = callMode === CallMode.SCRIPTED;
  const scriptMetadata = scriptId ? scriptRegistry[scriptId]?.metadata : null;

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #E0E0E0',
        px: 3,
        py: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {/* Left side - Agent info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Avatar with speaking indicator */}
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                overflow: 'hidden',
                border: isSpeaking ? '3px solid #4CAF50' : '3px solid transparent',
                transition: 'border 0.3s ease',
                animation: isSpeaking ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
                  },
                },
              }}
            >
              <img
                src={
                  agentInfo?.profile?.avatar ||
                  'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp'
                }
                alt={agentInfo?.profile?.name || 'Rachel'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
            {isSpeaking && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 14,
                  height: 14,
                  bgcolor: '#4CAF50',
                  borderRadius: '50%',
                  border: '2px solid white',
                }}
              />
            )}
          </Box>

          {/* Agent details */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#333',
                fontSize: '18px',
                lineHeight: 1.2,
              }}
            >
              {agentInfo?.profile?.name || 'Rachel'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#4CAF50',
                  animation: 'blink 2s infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: '#666', fontSize: '14px' }}
              >
                On call • {callDuration}
              </Typography>
            </Box>
          </Box>

          {/* Call type badge */}
          <Chip
            label={isScriptedMode ? (scriptMetadata?.title || 'Annual Policy Review') : 'Insurance Consultation'}
            size="small"
            sx={{
              ml: 2,
              bgcolor: isScriptedMode ? '#FFF3E0' : '#E3F2FD',
              color: isScriptedMode ? '#E65100' : '#1565C0',
              fontWeight: 500,
              border: 'none',
            }}
          />
        </Box>

        {/* Right side - Call controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Mute button */}
          {onToggleMute && (
            <IconButton
              onClick={onToggleMute}
              sx={{
                width: 44,
                height: 44,
                bgcolor: isMuted ? '#FFE0E0' : '#F5F5F5',
                color: isMuted ? '#D32F2F' : '#666',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isMuted ? '#FFCDD2' : '#E0E0E0',
                  transform: 'scale(1.05)',
                },
              }}
            >
              {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
            </IconButton>
          )}

          {/* End call button */}
          <IconButton
            onClick={() => {
              onEndCall();
              // Also refresh the page for scripted mode
              if (callMode === CallMode.SCRIPTED) {
                setTimeout(() => window.location.reload(), 100);
              }
            }}
            sx={{
              width: 44,
              height: 44,
              bgcolor: '#F44336',
              color: 'white',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#D32F2F',
                transform: 'scale(1.05)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <Phone size={20} style={{ transform: 'rotate(135deg)' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};